import { faPuzzlePiece } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { useComponentRegistry } from '../../../registry/ComponentRegistry';
import type { FieldConfiguration } from '../../../types';

interface CustomComponentConfiguratorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
}

export function CustomComponentConfigurator({
	field,
	onUpdateField,
	isModalOpen = false,
}: CustomComponentConfiguratorProps) {
	const colors = useThemeColors();
	const { components: customComponents } = useComponentRegistry();

	if (field.displayStyle !== 'custom' || field.customComponent?.id == null) {
		return null;
	}

	const customComponent = customComponents.find(
		(c) => c.id === field.customComponent?.id,
	);
	if (!customComponent) {
		return null;
	}

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-3">
					<Icon
						icon={faPuzzlePiece}
						size="sm"
						color={colors.values.textPrimary}
						className="mr-2"
					/>
					<Text className="text-base font-semibold">
						{customComponent.displayName} Configuration
					</Text>
				</div>

				{customComponent.description && (
					<Text className="text-sm mb-3" color="secondary">
						{customComponent.description}
					</Text>
				)}

				{customComponent.configOptions &&
					customComponent.configOptions.length > 0 && (
						<div className="space-y-3">
							{customComponent.configOptions.map((option) => (
								<div key={option.name}>
									<Text className="text-sm font-medium mb-1">
										{option.label}
									</Text>
									{option.description && (
										<Text className="text-xs mb-1" color="secondary">
											{option.description}
										</Text>
									)}
									{option.type === 'boolean' && (
										<button
											onClick={() => {
												onUpdateField({
													customComponent: {
														id: field.customComponent?.id ?? '',
														props: {
															...field.customComponent?.props,
															[option.name]:
																!field.customComponent?.props?.[option.name],
														},
													},
												});
											}}
											className={cn(
												'flex items-center px-3 py-2 rounded-md border transition-colors',
												field.customComponent?.props?.[option.name]
													? cn(
															colors.classes.bgInfo,
															colors.classes.borderPrimary,
														)
													: cn(
															colors.classes.bgSecondary,
															colors.classes.borderDefault,
															colors.classes.bgPrimaryHover,
														),
											)}
										>
											<div
												className={cn(
													'w-4 h-4 rounded border-2 mr-2',
													colors.classes.bgPrimary,
													field.customComponent?.props?.[option.name]
														? colors.classes.borderPrimary
														: colors.classes.borderDefault,
												)}
											/>
											<Text
												className={cn(
													'text-sm',
													field.customComponent?.props?.[option.name] &&
														'font-semibold',
												)}
											>
												{field.customComponent?.props?.[option.name]
													? 'Enabled'
													: 'Disabled'}
											</Text>
										</button>
									)}
									{option.type === 'string' && (
										<Input
											type="text"
											value={
												(field.customComponent?.props?.[option.name] as
													| string
													| undefined) ?? ''
											}
											onChange={(e) => {
												onUpdateField({
													customComponent: {
														id: field.customComponent?.id ?? '',
														props: {
															...field.customComponent?.props,
															[option.name]: e.target.value,
														},
													},
												});
											}}
											placeholder={option.defaultValue?.toString() ?? ''}
											size="sm"
										/>
									)}
									{option.type === 'number' && (
										<Input
											type="number"
											value={
												(
													field.customComponent?.props?.[option.name] as
														| number
														| undefined
												)?.toString() ?? ''
											}
											onChange={(e) => {
												const num = parseFloat(e.target.value);

												if (!isNaN(num) || e.target.value === '') {
													onUpdateField({
														customComponent: {
															id: field.customComponent?.id ?? '',
															props: {
																...field.customComponent?.props,
																[option.name]:
																	e.target.value === '' ? undefined : num,
															},
														},
													});
												}
											}}
											placeholder={option.defaultValue?.toString() ?? ''}
											size="sm"
										/>
									)}
									{option.type === 'select' && option.options != null && (
										<Select
											value={
												field.customComponent?.props?.[option.name] as
													| string
													| undefined
											}
											options={option.options}
											onChange={(value) => {
												onUpdateField({
													customComponent: {
														id: field.customComponent?.id ?? '',
														props: {
															...field.customComponent?.props,
															[option.name]: value,
														},
													},
												});
											}}
											placeholder="Select..."
										/>
									)}
								</div>
							))}
						</div>
					)}
			</div>
		</Card>
	);
}
